import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { cCache } from './c-cache';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { useAuth } from 'react-oidc-context';
import { getPath as getSmartSendApiPath } from '~/lib/api/smartsend/get-path';
import { authedFetch } from '~/lib/api/authed-fetch';
import { GetCountriesResponse } from '~/lib/api/smartsend/types';
import { parseResult } from '~/lib/api/parse-result';
import { getApiPath as getMeApiPath } from '~/lib/api/me/get-api-path';
import { GetCountyOptionsResponse } from '~/lib/api/me/types';

const useCountries = () => {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const { t } = useTranslation(TranslationNamespaces.COUNTRY);

  const translateCountry = useCallback(
    (name: string, isoCode: string, subdivision?: string) =>
      t(subdivision ? `Country_${isoCode}-${subdivision}` : `Country_${isoCode}`, name),
    [t]
  );

  const getCachedCountries = useCallback(async () => {
    if (cCache.countries.length) {
      return cCache.countries;
    }

    if (!user?.access_token) {
      return [];
    }

    const url = getSmartSendApiPath(tenant, `/country/list`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    const res = await parseResult<GetCountriesResponse>(response);

    if (res.success) {
      const cachable = res.result.Countries;
      cCache.countries = cachable;
      return cachable;
    }
    return [];
  }, [tenant, user]);

  const getLocalisedCountries = useCallback(async () => {
    const res = await getCachedCountries();

    return res.map((country) => ({
      ...country,
      LocalisedName: translateCountry(country.Name, country.CountryIsoCode, country.CountrySubdivision)
    }));
  }, [getCachedCountries, translateCountry]);

  const getCachedAddressCountryOptions = useCallback(async () => {
    if (cCache.countryOptions.length) {
      return cCache.countryOptions;
    }

    if (!user?.access_token) {
      return [];
    }

    const url = getMeApiPath(tenant, `/address/country-options`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    const res = await parseResult<GetCountyOptionsResponse>(response);

    if (res.success) {
      const cachable = res.result.countries;
      cCache.countryOptions = cachable;
      return cachable;
    }
    return [];
  }, [tenant, user?.access_token]);

  const getLocalisedAddressCountryOptions = useCallback(async () => {
    const res = await getCachedAddressCountryOptions();
    return res.map((country) => ({
      ...country,
      localisedName: translateCountry(country.name, country.iso3Code, country.subdivision)
    }));
  }, [getCachedAddressCountryOptions, translateCountry]);

  return {
    getLocalisedCountries: getLocalisedCountries,
    getLocalisedAddressCountryOptions: getLocalisedAddressCountryOptions,
    translateCountry: translateCountry
  } as const;
};

export default useCountries;
